import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Briefcase, Plus, Pencil, Trash2, Search, ChevronRight, ChevronLeft, UserPlus, X, User } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Cargos = () => {
  const { isAdmin, token } = useAuth();
  const [cargos, setCargos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [gerencias, setGerencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterNivel, setFilterNivel] = useState('all');
  const [filterArea, setFilterArea] = useState('all');
  const [filterGerencia, setFilterGerencia] = useState('all');
  const [filterSuperior, setFilterSuperior] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCargo, setEditingCargo] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    nivel: 1,
    cargo_superior_id: '',
    area_id: '',
    gerencia_id: '',
    es_staff: false,
    personas_asignadas: []
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, itemId: null, deps: [], loadingDeps: false });
  const [assignModal, setAssignModal] = useState({ open: false, cargoId: null, cargoNombre: '' });
  const [personaSearch, setPersonaSearch] = useState('');
  const [formPersonaSearch, setFormPersonaSearch] = useState('');
  const [formSuperiorSearch, setFormSuperiorSearch] = useState('');
  const [allPersonas, setAllPersonas] = useState([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  const toTitleCase = (str) => {
    if (!str) return str;
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [cargosRes, areasRes, gerenciasRes] = await Promise.all([
        axios.get(`${API}/cargos-con-personas`, { headers }),
        axios.get(`${API}/areas`, { headers }),
        axios.get(`${API}/gerencias`, { headers })
      ]);
      setCargos(cargosRes.data);
      setAreas(areasRes.data);
      setGerencias(gerenciasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const filteredCargos = cargos.filter(cargo => {
    const personaNombres = (cargo.personas || []).map(p => `${p.nombre} ${p.apellido} ${p.rut || ''}`).join(' ').toLowerCase();
    const searchable = `${cargo.nombre} ${personaNombres}`.toLowerCase();
    const words = search.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const matchSearch = words.length === 0 || words.every(w => searchable.includes(w));
    const matchNivel = filterNivel === 'all' || cargo.nivel === parseInt(filterNivel);
    const matchArea = filterArea === 'all' || cargo.area_id === filterArea;
    const matchGerencia = filterGerencia === 'all' ||
      (filterGerencia === 'none' ? !cargo.gerencia_id : cargo.gerencia_id === filterGerencia);
    const matchSuperior = filterSuperior === 'all' ||
      (filterSuperior === 'none' ? !cargo.cargo_superior_id : cargo.cargo_superior_id === filterSuperior);
    return matchSearch && matchNivel && matchArea && matchGerencia && matchSuperior;
  });

  const niveles = [...new Set(cargos.map(c => c.nivel))].sort((a, b) => a - b);
  const superiores = cargos.filter(c => cargos.some(x => x.cargo_superior_id === c.id));

  const totalPages = Math.ceil(filteredCargos.length / PAGE_SIZE);
  const paginatedCargos = filteredCargos.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreateDialog = async () => {
    setEditingCargo(null);
    setFormData({ nombre: '', nivel: 1, cargo_superior_id: '', area_id: '', gerencia_id: '', es_staff: false, personas_asignadas: [] });
    setFormPersonaSearch('');
    setFormSuperiorSearch('');
    setIsDialogOpen(true);
    if (allPersonas.length === 0) {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/personas`, { headers });
        setAllPersonas(Array.isArray(res.data) ? res.data : res.data?.data || []);
      } catch {}
    }
  };

  const openEditDialog = async (cargo) => {
    setEditingCargo(cargo);
    setFormData({
      nombre: cargo.nombre,
      nivel: cargo.nivel,
      cargo_superior_id: cargo.cargo_superior_id || '',
      area_id: cargo.area_id || '',
      gerencia_id: cargo.gerencia_id || '',
      es_staff: cargo.es_staff || false,
      personas_asignadas: (cargo.personas || []).map(p => ({ id: p.id, nombre: p.nombre, apellido: p.apellido, rut: p.rut }))
    });
    setFormPersonaSearch('');
    const sup = cargo.cargo_superior_id ? cargos.find(c => c.id === cargo.cargo_superior_id) : null;
    setFormSuperiorSearch(sup ? sup.nombre : '');
    setIsDialogOpen(true);
    if (allPersonas.length === 0) {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/personas`, { headers });
        setAllPersonas(Array.isArray(res.data) ? res.data : res.data?.data || []);
      } catch {}
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        nombre: formData.nombre,
        nivel: formData.nivel,
        cargo_superior_id: formData.cargo_superior_id || null,
        area_id: formData.area_id || null,
        gerencia_id: formData.gerencia_id || null,
        es_staff: formData.es_staff
      };
      let cargoId = editingCargo?.id;
      if (editingCargo) {
        await axios.put(`${API}/cargos/${editingCargo.id}`, payload, { headers });
        toast.success('Cargo actualizado');
      } else {
        const res = await axios.post(`${API}/cargos`, payload, { headers });
        cargoId = res.data.id;
        toast.success('Cargo creado');
      }
      // Sync personas asignadas
      if (cargoId) {
        const prevIds = new Set((editingCargo?.personas || []).map(p => p.id));
        const newIds = new Set(formData.personas_asignadas.map(p => p.id));
        // Desasignar las que se quitaron
        for (const p of (editingCargo?.personas || [])) {
          if (!newIds.has(p.id)) {
            await axios.put(`${API}/cargos/${cargoId}/desasignar-persona`, { persona_id: p.id }, { headers });
          }
        }
        // Asignar las nuevas
        for (const p of formData.personas_asignadas) {
          if (!prevIds.has(p.id)) {
            await axios.put(`${API}/cargos/${cargoId}/asignar-persona`, { persona_id: p.id }, { headers });
          }
        }
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    setDeleteDialog(prev => ({ ...prev, open: true, itemId: id, deps: [], loadingDeps: true }));
    try {
      const res = await axios.get(`${API}/dependencias/cargos/${id}`);
      setDeleteDialog(prev => ({ ...prev, deps: res.data.dependencias, loadingDeps: false }));
    } catch { setDeleteDialog(prev => ({ ...prev, loadingDeps: false })); }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/cargos/${deleteDialog.itemId}`);
      toast.success('Cargo eliminado');
      setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
      setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false });
    }
  };

  const getAreaName = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area?.nombre || '-';
  };

  const getGerenciaName = (gerenciaId) => {
    const g = gerencias.find(a => a.id === gerenciaId);
    return g?.nombre || '-';
  };

  const getCargoSuperior = (cargoId) => {
    const cargo = cargos.find(c => c.id === cargoId);
    return cargo?.nombre || '-';
  };

  const openAssignModal = async (cargo) => {
    setAssignModal({ open: true, cargoId: cargo.id, cargoNombre: cargo.nombre });
    setPersonaSearch('');
    setLoadingPersonas(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/personas`, { headers });
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setAllPersonas(list);
    } catch { setAllPersonas([]); }
    setLoadingPersonas(false);
  };

  const handleAssign = async (personaId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/cargos/${assignModal.cargoId}/asignar-persona`, { persona_id: personaId }, { headers });
      toast.success('Persona asignada al cargo');
      fetchData();
    } catch { toast.error('Error al asignar'); }
  };

  const handleUnassign = async (cargoId, personaId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/cargos/${cargoId}/desasignar-persona`, { persona_id: personaId }, { headers });
      toast.success('Persona desasignada');
      fetchData();
    } catch { toast.error('Error al desasignar'); }
  };

  const filteredModalPersonas = allPersonas.filter(p => {
    const txt = `${p.nombre} ${p.apellido} ${p.rut || ''}`.toLowerCase();
    const words = personaSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every(w => txt.includes(w));
  }).slice(0, 20);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Cargos</h1>
          <p className="text-slate-500">Gestión de cargos y niveles organizacionales</p>
        </div>
        {isAdmin() && (
          <Button 
            onClick={openCreateDialog}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="create-cargo-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cargo
          </Button>
        )}
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar cargo o persona..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="search-cargos"
              />
            </div>
            <Select value={filterNivel} onValueChange={(v) => { setFilterNivel(v); setPage(1); }}>
              <SelectTrigger className="w-[140px]" data-testid="filter-nivel">
                <SelectValue placeholder="Nivel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los niveles</SelectItem>
                {niveles.map(n => (
                  <SelectItem key={n} value={String(n)}>Nivel {n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterArea} onValueChange={(v) => { setFilterArea(v); setPage(1); }}>
              <SelectTrigger className="w-[180px]" data-testid="filter-area">
                <SelectValue placeholder="Area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las areas</SelectItem>
                {areas.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterGerencia} onValueChange={(v) => { setFilterGerencia(v); setPage(1); }}>
              <SelectTrigger className="w-[200px]" data-testid="filter-gerencia">
                <SelectValue placeholder="Gerencia" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las gerencias</SelectItem>
                <SelectItem value="none">Sin gerencia</SelectItem>
                {gerencias.map(g => (
                  <SelectItem key={g.id} value={g.id}>{toTitleCase(g.nombre)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSuperior} onValueChange={(v) => { setFilterSuperior(v); setPage(1); }}>
              <SelectTrigger className="w-[200px]" data-testid="filter-superior">
                <SelectValue placeholder="Cargo superior" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los superiores</SelectItem>
                <SelectItem value="none">Sin superior</SelectItem>
                {superiores.map(c => (
                  <SelectItem key={c.id} value={c.id}>{toTitleCase(c.nombre)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(filterNivel !== 'all' || filterArea !== 'all' || filterGerencia !== 'all' || filterSuperior !== 'all') && (
              <Button variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-slate-700"
                onClick={() => { setFilterNivel('all'); setFilterArea('all'); setFilterGerencia('all'); setFilterSuperior('all'); setPage(1); }}
                data-testid="clear-filters-btn">
                Limpiar filtros
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cargo</TableHead>
                <TableHead>Nivel</TableHead>
                <TableHead>Gerencia</TableHead>
                <TableHead>Persona</TableHead>
                <TableHead>Cargo Superior</TableHead>
                {isAdmin() && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedCargos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                    No hay cargos registrados
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCargos.map((cargo) => (
                  <TableRow key={cargo.id} data-testid={`cargo-row-${cargo.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Briefcase className="w-4 h-4 text-blue-600" />
                        </div>
                        {toTitleCase(cargo.nombre)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
                        Nivel {cargo.nivel}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{cargo.gerencia_id ? toTitleCase(getGerenciaName(cargo.gerencia_id)) : <span className="text-slate-400">-</span>}</TableCell>
                    <TableCell>
                      {cargo.personas?.length > 0 ? (
                        <div className="space-y-1">
                          {cargo.personas.map(p => (
                            <div key={p.id} className="flex items-center gap-1.5 group">
                              <User className="w-3 h-3 text-teal-500 shrink-0" />
                              <span className="text-xs text-slate-700">{toTitleCase(`${p.nombre} ${p.apellido}`)}</span>
                              {isAdmin() && (
                                <button onClick={(e) => { e.stopPropagation(); handleUnassign(cargo.id, p.id); }}
                                  className="opacity-0 group-hover:opacity-100 ml-1 text-red-400 hover:text-red-600 transition-opacity" title="Desasignar">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        isAdmin() ? (
                          <button onClick={() => openAssignModal(cargo)}
                            className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 hover:bg-teal-50 px-2 py-1 rounded transition-colors"
                            data-testid={`assign-persona-${cargo.id}`}>
                            <UserPlus className="w-3 h-3" /> Asignar
                          </button>
                        ) : <span className="text-slate-400 text-xs">Sin asignar</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {cargo.cargo_superior_id ? (
                        <div className="flex items-center gap-1 text-slate-600">
                          <ChevronRight className="w-4 h-4" />
                          {toTitleCase(getCargoSuperior(cargo.cargo_superior_id))}
                        </div>
                      ) : (
                        <span className="text-slate-400">Sin superior</span>
                      )}
                    </TableCell>
                    {isAdmin() && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openAssignModal(cargo)} title="Asignar persona" data-testid={`assign-btn-${cargo.id}`}>
                            <UserPlus className="w-4 h-4 text-teal-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(cargo)} data-testid={`edit-cargo-${cargo.id}`}>
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(cargo.id)} data-testid={`delete-cargo-${cargo.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <p className="text-sm text-slate-500">
                Mostrando {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredCargos.length)} de {filteredCargos.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <Button
                    key={p}
                    variant={p === page ? 'default' : 'outline'}
                    size="sm"
                    className={p === page ? 'bg-teal-500 hover:bg-teal-600' : ''}
                    onClick={() => setPage(p)}
                    data-testid={`page-${p}-btn`}
                  >
                    {p}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCargo ? 'Editar Cargo' : 'Nuevo Cargo'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre del Cargo</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
                data-testid="cargo-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nivel">Nivel Organizacional</Label>
              <Input
                id="nivel"
                type="number"
                min="1"
                value={formData.nivel}
                onChange={(e) => setFormData({ ...formData, nivel: parseInt(e.target.value) || 1 })}
                required
                data-testid="cargo-nivel-input"
              />
            </div>
            <div className="flex items-center gap-3 py-1">
              <input
                type="checkbox"
                id="es_staff"
                checked={formData.es_staff}
                onChange={(e) => setFormData({ ...formData, es_staff: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 text-teal-500 focus:ring-teal-500"
                data-testid="cargo-staff-check"
              />
              <Label htmlFor="es_staff" className="cursor-pointer text-sm">
                Posicion Staff (lateral en organigrama)
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="gerencia">Gerencia</Label>
              <Select
                value={formData.gerencia_id || 'none'}
                onValueChange={(value) => setFormData({ ...formData, gerencia_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger data-testid="cargo-gerencia-select">
                  <SelectValue placeholder="Sin gerencia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin gerencia</SelectItem>
                  {gerencias.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {toTitleCase(g.nombre)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="area">Area (Opcional)</Label>
              <Select
                value={formData.area_id || 'none'}
                onValueChange={(value) => setFormData({ ...formData, area_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger data-testid="cargo-area-select">
                  <SelectValue placeholder="Sin area" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin area</SelectItem>
                  {areas.map((area) => (
                    <SelectItem key={area.id} value={area.id}>
                      {area.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cargo_superior">Cargo Superior (Opcional)</Label>
              {formData.cargo_superior_id ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
                  <Briefcase className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-sm text-slate-800 flex-1">{formSuperiorSearch}</span>
                  <button type="button" onClick={() => { setFormData(f => ({ ...f, cargo_superior_id: '' })); setFormSuperiorSearch(''); }}
                    className="text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    placeholder="Buscar cargo o persona..."
                    value={formSuperiorSearch}
                    onChange={(e) => setFormSuperiorSearch(e.target.value)}
                    className="pl-9 text-sm"
                    data-testid="cargo-superior-search"
                  />
                </div>
              )}
              {!formData.cargo_superior_id && formSuperiorSearch.length >= 2 && (
                <div className="max-h-[150px] overflow-y-auto border rounded-lg divide-y text-sm">
                  {cargos.filter(c => {
                    if (c.id === editingCargo?.id) return false;
                    const cargoNombre = c.nombre.toLowerCase();
                    const personasNombres = (c.personas || []).map(p => `${p.nombre} ${p.apellido}`).join(' ').toLowerCase();
                    const searchable = `${cargoNombre} ${personasNombres}`;
                    const words = formSuperiorSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
                    return words.every(w => searchable.includes(w));
                  }).slice(0, 10).map(c => {
                    const persona = c.personas?.[0];
                    return (
                      <button type="button" key={c.id}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                        onClick={() => { setFormData(f => ({ ...f, cargo_superior_id: c.id })); setFormSuperiorSearch(c.nombre); }}>
                        <span className="text-sm font-medium text-slate-800">{toTitleCase(c.nombre)}</span>
                        {persona && <span className="text-xs text-slate-400 ml-2">({toTitleCase(`${persona.nombre} ${persona.apellido}`)})</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Personas asignadas</Label>
              {/* Chips de personas ya asignadas */}
              {formData.personas_asignadas.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {formData.personas_asignadas.map(p => (
                    <div key={p.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 border border-teal-200 rounded-lg">
                      <User className="w-3 h-3 text-teal-600" />
                      <span className="text-xs text-teal-800">{toTitleCase(`${p.nombre} ${p.apellido}`)}</span>
                      <button type="button" onClick={() => setFormData(f => ({ ...f, personas_asignadas: f.personas_asignadas.filter(x => x.id !== p.id) }))}
                        className="text-teal-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              {/* Buscador para agregar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  placeholder="Buscar persona por nombre o RUT..."
                  value={formPersonaSearch}
                  onChange={(e) => setFormPersonaSearch(e.target.value)}
                  className="pl-9 text-sm"
                  data-testid="form-persona-search"
                />
              </div>
              {formPersonaSearch.length >= 2 && (
                <div className="max-h-[150px] overflow-y-auto border rounded-lg divide-y text-sm">
                  {allPersonas.filter(p => {
                    if (formData.personas_asignadas.some(x => x.id === p.id)) return false;
                    const txt = `${p.nombre} ${p.apellido} ${p.rut || ''}`.toLowerCase();
                    const words = formPersonaSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
                    return words.every(w => txt.includes(w));
                  }).slice(0, 8).map(p => (
                    <button type="button" key={p.id}
                      className="w-full text-left px-3 py-2 hover:bg-teal-50 transition-colors flex items-center justify-between"
                      onClick={() => { setFormData(f => ({ ...f, personas_asignadas: [...f.personas_asignadas, { id: p.id, nombre: p.nombre, apellido: p.apellido, rut: p.rut }] })); setFormPersonaSearch(''); }}>
                      <span>{toTitleCase(`${p.nombre} ${p.apellido}`)}</span>
                      <span className="text-xs text-slate-400">{p.rut || ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-500 hover:bg-teal-600" data-testid="save-cargo-btn">
                {editingCargo ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onOpenChange={(v) => !v && setDeleteDialog({ open: false, itemId: null, deps: [], loadingDeps: false })}
        onConfirm={confirmDelete}
        title="Eliminar Cargo"
        description="¿Esta seguro de eliminar este cargo? Esta accion no se puede deshacer."
        dependencies={deleteDialog.deps}
        loadingDeps={deleteDialog.loadingDeps}
      />

      {/* Modal Asignar Persona */}
      <Dialog open={assignModal.open} onOpenChange={(v) => !v && setAssignModal({ open: false, cargoId: null, cargoNombre: '' })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-teal-500" />
              Asignar Persona a {toTitleCase(assignModal.cargoNombre)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, apellido o RUT..."
                value={personaSearch}
                onChange={(e) => setPersonaSearch(e.target.value)}
                className="pl-9"
                autoFocus
                data-testid="assign-persona-search"
              />
            </div>
            <div className="max-h-[350px] overflow-y-auto border rounded-lg divide-y">
              {loadingPersonas ? (
                <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-b-2 border-teal-500 rounded-full" /></div>
              ) : filteredModalPersonas.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-6">No se encontraron personas</p>
              ) : (
                filteredModalPersonas.map(p => {
                  const yaAsignado = cargos.find(c => c.id === assignModal.cargoId)?.personas?.some(x => x.id === p.id);
                  return (
                    <div key={p.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{toTitleCase(`${p.nombre} ${p.apellido}`)}</p>
                        <p className="text-xs text-slate-400">{p.rut || ''}{p.cargo_nombre ? ` · ${toTitleCase(p.cargo_nombre)}` : ''}</p>
                      </div>
                      {yaAsignado ? (
                        <span className="text-xs text-emerald-600 font-medium px-2 py-1 bg-emerald-50 rounded">Asignado</span>
                      ) : (
                        <Button size="sm" variant="outline" className="text-xs h-7 border-teal-300 text-teal-600 hover:bg-teal-50"
                          onClick={() => handleAssign(p.id)}
                          data-testid={`assign-btn-persona-${p.id}`}>
                          Asignar
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            {!loadingPersonas && personaSearch && filteredModalPersonas.length === 20 && (
              <p className="text-xs text-slate-400 text-center">Mostrando primeros 20 resultados. Refina tu busqueda.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
